// Seismic overlay — USGS earthquake events as MIL-STD-2525-style icons,
// severity-scaled by magnitude (already encoded as `severity` 0..100 by
// the USGS connector: M2 → 0, M9 → 100).
//
// Sits on top of war-zones and conflicts in the composition order so a
// quake inside a conflict zone is still legible. Visible from low zoom
// because notable seismic events (M5+) are inherently regional / global
// news — operators want to see them at country zoom.

import type { intel as i } from '@eidollon/contracts';
import { buildIconLayer } from './_icon-builder.js';
import { withGeom } from './_shared.js';
import type { LayerSpec } from './registry.js';

const TYPES: readonly i.SignalType[] = ['SEI'];
const TYPE_SET = new Set<i.SignalType>(TYPES);

export const seismicLayer: LayerSpec = {
  id: 'seismic',
  label: 'SEI · seismic',
  kind: 'icon',
  signalTypes: TYPES,
  color: 'var(--cyan)',
  colorAxis: 'severity',
  description: 'USGS earthquakes, severity-scaled by magnitude (M2..M9)',
  classification: 'OPEN',
  legend: {
    swatch: 'var(--cyan)',
    label: 'SEI',
    hint: 'USGS earthquakes (mag → severity)',
  },
  select: (rows) => withGeom(rows).filter((r) => TYPE_SET.has(r.signal_type)),
  // Visible from country zoom upward — significant seismic events deserve
  // wide-context visibility, unlike the city-level cyber/contract layers.
  // Always visible — the feed is capped at ~200 rows so there is no clutter,
  // and a single-type filter must show that type at ANY zoom (otherwise the
  // map goes empty below the old threshold). See route bug: "toggle on -> disappear".
  visibleAtZoom: () => true,
  deckLayer: ({ rows, viewport, onPick }) => {
    const data = withGeom(rows).filter((r) => TYPE_SET.has(r.signal_type));
    return buildIconLayer({
      id: 'intel-seismic',
      rows: data,
      zoom: viewport.zoom,
      sizeMin: 8,
      sizeMax: 28,
      onPick,
    });
  },
};
